import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";

import { Helmet } from "react-helmet-async";

import {
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card as MuiCard,
  CardContent,
  Chip as MuiChip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider as MuiDivider,
  Fade,
  FormControlLabel as MuiFormControlLabel,
  Grid,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { Add as AddIcon } from "@material-ui/icons";
import * as yup from "yup";
import { spacing } from "@material-ui/system";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store/reducer";
import { IPlacesState } from "../redux/reducers/placesReducer";
import {
  addMorePlaces,
  addPlace,
  getPlaces,
  getTotalDocs,
  openEditPlaceDialog,
  openNewPlaceDialog,
  removePlace,
  setPlaceDialogOpen,
  updatePlace,
} from "../redux/actions/placesActions";
import { Skeleton } from "@material-ui/lab";
import { green, red } from "@material-ui/core/colors";
import { useFormik } from "formik";
import { Edit as EditIcon, Trash2 as RemoveIcon } from "react-feather";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DialogAction } from "../models/dialog-actions";
import { useStyles } from "../theme/useStyles";
import { TABLE_LIMITS, TABLE_LIMIT_DEFAULT } from "../constants";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const FormControlLabel = styled(MuiFormControlLabel)(spacing);

const Chip: any = styled(MuiChip)`
  ${spacing};

  background: ${(props: any) => props.activated && green[500]};
  background: ${(props: any) => props.desactivated && red[500]};
  color: ${(props) => props.theme.palette.common.white};
  min-width: 90px;
`;

function ContentCard() {
  const { places, isLoading, totalDocs } = useSelector<RootState, IPlacesState>(
    (state) => state.placesReducer
  );
  const classes = useStyles();
  const dispatch = useDispatch();

  const [limit, setLimit] = useState(TABLE_LIMIT_DEFAULT);
  const [page, setPage] = useState(0);

  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));

    if (parseInt(event.target.value, 10) > places.length) {
      dispatch(getPlaces(parseInt(event.target.value, 10)));
    }
    setPage(0);
  };

  const handlePageChange = (_: any, newPage: number) => {
    if (places.length < totalDocs && newPage > page) {
      dispatch(addMorePlaces(limit));
    }
    setPage(newPage);
  };

  // Confirm Dialog
  const [dialogState, setDialogState] = useState({
    open: false,
    title: "",
    place: {},
  });

  const handleRemoveButton = (place: any) => {
    setDialogState((dialogState) => ({
      ...dialogState,
      title: `¿Desea eliminar a al recinto ${place.Nombre}?`,
      place: place,
      open: true,
    }));
  };

  const handleEditButton = (place: any) => {
    dispatch(openEditPlaceDialog(place));
  };

  return (
    <Card mb={6}>
      <CardContent>
        <TableContainer className={classes.tableContainer}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading
                ? places
                    .slice(page * limit, page * limit + limit)
                    .map((place) => (
                      <Fade key={place.id} in={true}>
                        <TableRow hover className={classes.styledRow}>
                          <TableCell>{place.Nombre}</TableCell>
                          <TableCell>
                            {place.Activado ? (
                              <Chip
                                size="small"
                                mr={1}
                                mb={1}
                                label="Activado"
                                activated="true"
                              />
                            ) : (
                              <Chip
                                size="small"
                                mr={1}
                                mb={1}
                                label="Desactivado"
                                desactivated="true"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Editar">
                              <IconButton
                                onClick={() => handleEditButton(place)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                onClick={() => handleRemoveButton(place)}
                              >
                                <RemoveIcon color="red" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </Fade>
                    ))
                : new Array(limit).fill(0).map((x, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={5}>
                        <Skeleton />
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalDocs}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleLimitChange}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={TABLE_LIMITS}
        />
      </CardContent>
      <ConfirmDialog
        open={dialogState.open}
        onClose={() =>
          setDialogState((dialogState) => ({ ...dialogState, open: false }))
        }
        onConfirm={() => {
          dispatch(removePlace(dialogState.place));
        }}
        title={dialogState.title}
      />
    </Card>
  );
}

const DialogAddPlace = () => {
  const {
    dialog: { isDialogLoading, isDialogOpen, dialogAction, selectedPlace },
  } = useSelector<RootState, IPlacesState>((state) => state.placesReducer);

  const dispatch = useDispatch();

  const {
    handleSubmit,
    values,
    handleChange,
    touched,
    errors,
    setValues,
    resetForm,
  } = useFormik({
    initialValues: {
      Nombre: "",
      Activado: true,
    },
    onSubmit: (values) => {
      if (dialogAction === DialogAction.Edit) {
        dispatch(updatePlace({ ...values, id: selectedPlace.id }));
      }
      if (dialogAction === DialogAction.New) {
        dispatch(addPlace(values));
      }
    },
    validationSchema: yup.object({
      Nombre: yup.string().required("Nombre requerido").trim(),
    }),
  });

  useEffect(() => {
    setValues({
      Nombre: dialogAction === DialogAction.Edit ? selectedPlace?.Nombre : "",
      Activado:
        dialogAction === DialogAction.Edit ? selectedPlace?.Activado : false,
    });
    if (dialogAction === DialogAction.New) resetForm();
  }, [dialogAction, selectedPlace, resetForm, setValues]);

  return (
    <React.Fragment>
      <Dialog
        open={isDialogOpen}
        onClose={() => dispatch(setPlaceDialogOpen(false))}
        aria-labelledby="add-and-update-dialog-title"
      >
        <DialogTitle id="add-and-update-dialog-title">
          {dialogAction === DialogAction.Edit
            ? "Editar Recinto"
            : "Agregar Recinto"}
        </DialogTitle>

        <form noValidate onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              id="Nombre"
              label="Nombre Recinto"
              type="text"
              value={values.Nombre}
              name="Nombre"
              onChange={handleChange}
              error={touched.Nombre && Boolean(errors.Nombre)}
              helperText={touched.Nombre && errors.Nombre}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={values.Activado}
                  onChange={handleChange}
                  name="Activado"
                  color="primary"
                />
              }
              mt={4}
              label="Activado"
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => dispatch(setPlaceDialogOpen(false))}
              color="primary"
            >
              Cancelar
            </Button>
            <Button type="submit" color="primary" disabled={isDialogLoading}>
              {dialogAction === DialogAction.Edit ? "Editar" : "Agregar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  );
};

export const Places = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getPlaces());
    dispatch(getTotalDocs());
  }, [dispatch]);
  return (
    <React.Fragment>
      <Helmet title="Recintos" />
      <Grid justify="space-between" container>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Recintos
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Typography>Recintos</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                dispatch(openNewPlaceDialog());
              }}
            >
              <AddIcon />
              Nuevo Recinto
            </Button>
          </div>
        </Grid>
      </Grid>
      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <ContentCard />
        </Grid>
      </Grid>
      <DialogAddPlace />
    </React.Fragment>
  );
};
